import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { isPerfilInativo } from '../lib/statusConta';
import MaintenanceScreen from './MaintenanceScreen';

/**
 * Porteiro silencioso: usuário inativo enxerga a plataforma "em manutenção".
 *
 * Cobre as duas pontas:
 *  - quem já entra com o perfil inativado (o profile do AuthContext já diz);
 *  - quem é inativado com a sessão aberta — o ban do Auth só derruba no refresh
 *    do token (até 1h), então revalidamos o status ao voltar pra aba e a cada
 *    poucos minutos. Volta a ficar 'active'? O acesso é devolvido sem reload.
 *
 * Falha de rede/RLS não tranca ninguém: sem resposta confiável, deixa passar.
 */
const REVALIDACAO_MS = 3 * 60 * 1000;
// Voltar para a aba dispara `focus` e `visibilitychange` juntos, e o usuário alterna
// de janela o dia todo: no máximo uma checagem por minuto por esses eventos.
const INTERVALO_MINIMO_MS = 60 * 1000;

const MaintenanceGate = ({ children }: { children: React.ReactNode }) => {
    const { user, profile, signOut } = useAuth();
    const [statusRemoto, setStatusRemoto] = useState<{ inativo: boolean; verificadoEm: Date } | null>(null);

    const userId = user?.id;

    useEffect(() => {
        if (!userId) {
            setStatusRemoto(null);
            return;
        }

        let cancelado = false;
        let ultimaChecagem = 0;

        const revalidar = async () => {
            if (document.visibilityState === 'hidden') return;
            if (Date.now() - ultimaChecagem < INTERVALO_MINIMO_MS) return;
            ultimaChecagem = Date.now();
            const { data, error } = await supabase
                .from('profiles')
                .select('status')
                .eq('id', userId)
                .maybeSingle();
            if (cancelado || error || !data) return; // sem resposta confiável → não tranca
            setStatusRemoto({ inativo: isPerfilInativo(data.status), verificadoEm: new Date() });
        };

        revalidar();
        const intervalo = setInterval(revalidar, REVALIDACAO_MS);
        window.addEventListener('focus', revalidar);
        document.addEventListener('visibilitychange', revalidar);

        return () => {
            cancelado = true;
            clearInterval(intervalo);
            window.removeEventListener('focus', revalidar);
            document.removeEventListener('visibilitychange', revalidar);
        };
    }, [userId]);

    const sair = useCallback(() => { void signOut(); }, [signOut]);

    // Derivado no render: o status remoto (mais fresco) manda; sem ele, vale o profile.
    const emManutencao = !!user && (statusRemoto ? statusRemoto.inativo : isPerfilInativo(profile?.status));

    if (emManutencao) {
        return (
            <MaintenanceScreen
                marca={profile?.role === 'consumidor' ? 'portal' : 'vigorhub'}
                ultimaVerificacao={statusRemoto?.verificadoEm ?? new Date()}
                acao={{ label: 'Sair', onClick: sair }}
            />
        );
    }

    return <>{children}</>;
};

export default MaintenanceGate;
